/**
 * React hooks for subscription management.
 *
 * These hooks provide easy access to subscription status and entitlements
 * in React components, with proper loading states and error handling.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { authenticatedApi } from '../../lib/authenticated-api';
import { isSignedInAs } from '../../lib/auth/signedInAs';
import { getSubscriptionService } from './index';
import type {
  SubscriptionStatus,
  UserSubscription,
  PurchaseResult,
  BillingStatus,
} from './types';
import { PurchaseError } from './types';

/** Billing is observational; a failed read must never imply an active plan. */
export function useBillingStatus() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['billing-status', user?.uid],
    enabled: !!user?.uid,
    queryFn: async () => {
      const response =
        await authenticatedApi.request<BillingStatus>('/v1/billing/status');
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Unable to load billing status');
      }
      return response.data;
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to access subscription status and entitlements.
 *
 * @example
 * ```tsx
 * function PremiumFeature() {
 *   const { hasEntitlement, subscription, isLoading } = useSubscription();
 *
 *   if (isLoading) {
 *     return <LoadingSpinner />;
 *   }
 *
 *   if (!hasEntitlement('heapsterlive')) {
 *     return <UpgradePrompt />;
 *   }
 *
 *   return <PremiumContent />;
 * }
 * ```
 */
export function useSubscription(): SubscriptionStatus {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // The uid the state above was loaded for (#2218). The state held from before
  // a sign-in is the signed-out one (`isLoading: false`, no subscription), and
  // a new user's fetch only starts in an effect, after their first render. So
  // "loading" is also reported while the state is not yet this user's. A
  // failed read counts as loaded: `error` is the answer for that uid.
  const [loadedForUid, setLoadedForUid] = useState<string | null>(null);
  // Only the most recent fetch may write state, so an answer that arrives
  // late, for a user who is no longer signed in, is dropped.
  const latestFetch = useRef(0);

  // Fetch subscription data
  const fetchSubscription = useCallback(async () => {
    const fetchId = ++latestFetch.current;
    const isLatest = () => latestFetch.current === fetchId;

    if (!user) {
      setSubscription(null);
      setLoadedForUid(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const service = getSubscriptionService(user.uid);
      // Both are read for every signed-in user (#2218). `/entitlements` is
      // the only one that re-verifies a stale Stripe record with Stripe;
      // `/subscriptions` reads the stored record, where an expired one counts
      // as inactive. Reading entitlements only for a listed subscription read
      // a live subscriber with a missed renewal webhook as "not subscribed".
      const [sub, entitlements] = await Promise.all([
        service.getSubscription(),
        service.getActiveEntitlements(),
      ]);

      let finalSub: UserSubscription | null;
      if (sub?.entitlements) {
        // The service's subscription carries its own entitlements (the mock
        // service's does).
        finalSub = sub;
      } else if (Object.keys(entitlements).length > 0) {
        finalSub = { ...(sub ?? { uid: user.uid }), entitlements };
      } else if (sub) {
        // A listed subscription with no entitlements is a failed read, not a
        // subscription with nothing in it. `/subscriptions` lists one only
        // when an entitlement is active, and `/entitlements` is built from
        // the same document, so it is not empty for this user unless their
        // access ended between the two requests. But the backend fails safe
        // to `200 {}` when that read fails (`getActiveEntitlements` in
        // functions/src/services/entitlementService.ts), which arrives here
        // as an empty map. This rule exists only because of that fallback,
        // and it is here, not in a page, so that every consumer agrees.
        // Remove it once #2242 makes that read answer 5xx on failure.
        throw new Error(
          'Entitlements came back empty for a listed subscription'
        );
      } else {
        // Nothing listed and no entitlements: no subscription.
        finalSub = null;
      }

      if (isLatest()) {
        setSubscription(finalSub);
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      if (isLatest()) {
        setError(err as Error);
        setSubscription(null);
      }
    } finally {
      if (isLatest()) {
        setLoadedForUid(user.uid);
        setIsLoading(false);
      }
    }
  }, [user]);

  // Fetch on mount and when user changes
  useEffect(() => {
    void fetchSubscription();
  }, [fetchSubscription]);

  // Helper function to check entitlement
  const hasEntitlement = useCallback(
    (entitlementId: string): boolean => {
      if (!subscription || !subscription.entitlements) return false;

      const entitlement = subscription.entitlements[entitlementId];
      return entitlement?.isActive || false;
    },
    [subscription]
  );

  return {
    subscription,
    isLoading: isLoading || (!!user && loadedForUid !== user.uid),
    error,
    hasEntitlement,
    refetch: fetchSubscription,
  };
}

/**
 * Hook to handle subscription purchases.
 *
 * @example
 * ```tsx
 * function UpgradeButton() {
 *   const { purchase, isProcessing, error } = usePurchase();
 *
 *   const handleUpgrade = async () => {
 *     const result = await purchase('com.heapster.premium.monthly');
 *
 *     if (result.success) {
 *       toast.success('Welcome to Premium!');
 *       navigate('/dashboard');
 *     }
 *   };
 *
 *   return (
 *     <button
 *       onClick={handleUpgrade}
 *       disabled={isProcessing}
 *     >
 *       {isProcessing ? 'Processing...' : 'Upgrade to Premium'}
 *     </button>
 *   );
 * }
 * ```
 */
export function usePurchase() {
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const purchase = useCallback(
    async (productId: string): Promise<PurchaseResult> => {
      if (!user) {
        const error = new Error('Must be logged in to purchase');
        setError(error);
        throw error;
      }

      try {
        setIsProcessing(true);
        setError(null);

        const service = getSubscriptionService(user.uid);
        const result = await service.purchaseProduct(productId);

        if (!result.success && result.error) {
          // Keeps the backend's category (`errorCode`) with the message, so a
          // caller can tell an EMAIL_NOT_VERIFIED refusal from any other, and
          // the reason of a HEAPSTERLIVE_SUBSCRIPTION_EXISTS one (#2218).
          throw new PurchaseError(
            result.error,
            result.errorCode,
            result.conflictReason
          );
        }

        return result;
      } catch (err) {
        console.error('Purchase failed:', err);
        setError(err as Error);
        throw err;
      } finally {
        setIsProcessing(false);
      }
    },
    [user]
  );

  const restorePurchases = useCallback(async () => {
    if (!user) {
      const error = new Error('Must be logged in to restore purchases');
      setError(error);
      throw error;
    }

    try {
      setIsProcessing(true);
      setError(null);

      const service = getSubscriptionService(user.uid);
      await service.restorePurchases();
    } catch (err) {
      console.error('Restore purchases failed:', err);
      setError(err as Error);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  }, [user]);

  return {
    purchase,
    restorePurchases,
    isProcessing,
    error,
  };
}

const BILLING_PORTAL_ERROR_MESSAGE =
  'Unable to open billing portal. Please try again later.';

/**
 * Hook to open the Stripe billing portal for the signed-in user.
 *
 * Lifted from the subscription management page (#2218) so the Pricing page
 * and the Dashboard open the same portal the same way: one request, the same
 * return URL, the same toast when it fails. `isOpening` is true only while
 * the request is in flight. It is cleared on success too, so a page the
 * browser restores on Back from the portal does not come back disabled.
 * Redirects and error toasts are dropped if the requesting account is no
 * longer signed in.
 *
 * @example
 * ```tsx
 * function ManageButton() {
 *   const { openPortal, isOpening } = useBillingPortal();
 *
 *   return (
 *     <button onClick={() => void openPortal()} disabled={isOpening}>
 *       Manage subscription
 *     </button>
 *   );
 * }
 * ```
 */
export function useBillingPortal() {
  const { user } = useAuth();
  const [isOpening, setIsOpening] = useState(false);

  const openPortal = useCallback(async (): Promise<void> => {
    if (!user) return;
    const requestedBy = user.uid;

    setIsOpening(true);
    try {
      const response = await authenticatedApi.request<{
        url: string;
        return_url: string;
      }>('/v1/billing/portal', {
        method: 'POST',
        body: JSON.stringify({
          return_url: `${window.location.origin}/subscriptions`,
        }),
      });

      if (response.success && response.data?.url) {
        if (!isSignedInAs(requestedBy)) return;
        // Redirect to Stripe Customer Portal
        window.location.href = response.data.url;
        return;
      }

      console.error('Failed to create billing portal session:', response.error);
      if (isSignedInAs(requestedBy)) toast.error(BILLING_PORTAL_ERROR_MESSAGE);
    } catch (error) {
      console.error('Billing portal error:', error);
      if (isSignedInAs(requestedBy)) toast.error(BILLING_PORTAL_ERROR_MESSAGE);
    } finally {
      setIsOpening(false);
    }
  }, [user]);

  return { openPortal, isOpening };
}

/**
 * Hook to check a specific entitlement.
 * Useful for simple boolean checks without full subscription data.
 *
 * @example
 * ```tsx
 * function PremiumBadge() {
 *   const isPremium = useHasEntitlement('premium');
 *
 *   if (!isPremium) return null;
 *
 *   return <span className="badge-premium">PRO</span>;
 * }
 * ```
 */
export function useHasEntitlement(entitlementId: string): boolean {
  const { hasEntitlement, isLoading } = useSubscription();

  // Return false while loading to avoid flashing premium content
  if (isLoading) return false;

  return hasEntitlement(entitlementId);
}

/**
 * Hook to get entitlement expiration date.
 *
 * @example
 * ```tsx
 * function SubscriptionStatus() {
 *   const expirationDate = useEntitlementExpiration('premium');
 *
 *   if (!expirationDate) {
 *     return <p>No active subscription</p>;
 *   }
 *
 *   const daysRemaining = Math.ceil(
 *     (expirationDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
 *   );
 *
 *   return (
 *     <p>
 *       Premium expires in {daysRemaining} days
 *       {daysRemaining <= 7 && ' - Renew soon!'}
 *     </p>
 *   );
 * }
 * ```
 */
export function useEntitlementExpiration(entitlementId: string): Date | null {
  const { subscription } = useSubscription();

  if (!subscription || !subscription.entitlements) return null;

  const entitlement = subscription.entitlements[entitlementId];
  if (!entitlement?.isActive) return null;

  return entitlement.periodEndDate;
}

/**
 * Hook to mock premium access in development.
 * This should only be used for testing and will be removed in production.
 *
 * @example
 * ```tsx
 * // In a development testing component
 * function DevTools() {
 *   const { simulatePremium, clearPremium } = useMockPremium();
 *
 *   return (
 *     <div className="dev-tools">
 *       <button onClick={() => simulatePremium(30)}>
 *         Simulate 30-day Premium
 *       </button>
 *       <button onClick={clearPremium}>
 *         Clear Premium
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useMockPremium() {
  const { user } = useAuth();
  const { refetch } = useSubscription();

  const simulatePremium = useCallback(
    async (days = 30) => {
      if (!user) return;

      const service = getSubscriptionService(user.uid);
      if ('simulatePremiumAccess' in service) {
        // Type assertion for mock service method
        const mockService = service as {
          simulatePremiumAccess: (days: number) => void;
        };
        mockService.simulatePremiumAccess(days);
        await refetch();
      }
    },
    [user, refetch]
  );

  const clearPremium = useCallback(async () => {
    if (!user) return;

    const service = getSubscriptionService(user.uid);
    if ('clearMockSubscription' in service) {
      // Type assertion for mock service method
      const mockService = service as { clearMockSubscription: () => void };
      mockService.clearMockSubscription();
      await refetch();
    }
  }, [user, refetch]);

  // Only return functions in development
  if (process.env.NODE_ENV === 'production') {
    return {
      simulatePremium: () => {
        /* No-op in production */
      },
      clearPremium: () => {
        /* No-op in production */
      },
    };
  }

  return { simulatePremium, clearPremium };
}

/**
 * Hook to fetch available products and pricing information.
 * Uses TanStack Query for caching with a 5-minute stale time.
 *
 * @example
 * ```tsx
 * function PricingPage() {
 *   const { products, isLoading, error } = useProducts();
 *
 *   if (isLoading) {
 *     return <LoadingSpinner />;
 *   }
 *
 *   if (error) {
 *     return <ErrorMessage error={error} />;
 *   }
 *
 *   return (
 *     <div>
 *       {products.map(product => (
 *         <ProductCard key={product.productId} product={product} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useProducts() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      // Get service without authentication (products endpoint is public)
      const service = getSubscriptionService();
      return service.getProducts();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - pricing rarely changes
  });

  return {
    products: data?.products || [],
    detectedCurrency: data?.detectedCurrency,
    isLoading,
    error,
  };
}

/**
 * Hook to fetch all subscriptions for the current user (plural endpoint).
 * Uses TanStack Query v5 for efficient caching and state management.
 *
 * @example
 * ```tsx
 * function SubscriptionsPage() {
 *   const { subscriptions, isLoading, error, refetch } = useSubscriptions();
 *
 *   if (isLoading) {
 *     return <LoadingSpinner />;
 *   }
 *
 *   if (error) {
 *     return <ErrorMessage error={error} />;
 *   }
 *
 *   if (subscriptions.length === 0) {
 *     return <NoSubscriptionsMessage />;
 *   }
 *
 *   return (
 *     <div>
 *       {subscriptions.map(sub => (
 *         <SubscriptionCard key={sub.subscriptionId} subscription={sub} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useSubscriptions() {
  const { user } = useAuth();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['subscriptions', user?.uid],
    queryFn: async () => {
      if (!user?.uid) {
        return { subscriptions: [] };
      }

      const service = getSubscriptionService(user.uid);
      return service.getSubscriptions();
    },
    enabled: !!user?.uid,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
    retry: 2,
  });

  return {
    subscriptions: data?.subscriptions || [],
    isLoading,
    error,
    refetch,
  };
}
